import React, { useMemo, useState } from "react";
import {
  useGetSupportDashboard,
  useGetTicket,
  useUpdateTicket,
  useCreateTicketMessage,
  useListTicketAssignees,
  getGetSupportDashboardQueryKey,
  getGetTicketQueryKey,
  getListTicketsQueryKey,
  type TicketStatus,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Search,
  Filter,
  MoreVertical,
  Clock,
  UserCheck,
  Loader2,
  Send,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { useToast } from "@/hooks/use-toast";

const STATUS_OPTIONS: { value: TicketStatus; label: string }[] = [
  { value: "new", label: "New" },
  { value: "in_progress", label: "In Progress" },
  { value: "waiting_on_agent", label: "Waiting on Agent" },
  { value: "resolved", label: "Resolved" },
];

const UNASSIGNED = "unassigned";

export function SupportDashboard() {
  const { data: dashboard, isLoading, error } = useGetSupportDashboard();
  const { data: assignees } = useListTicketAssignees();
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("all");

  const assigneeMap = new Map((assignees ?? []).map((a) => [a.id, a.name]));

  const filteredTickets = useMemo(() => {
    const tickets = dashboard?.openTickets ?? [];
    const q = query.trim().toLowerCase();
    return tickets.filter((t) => {
      const matchesPriority = priorityFilter === "all" || t.priority === priorityFilter;
      const matchesQuery =
        !q ||
        t.subject.toLowerCase().includes(q) ||
        t.reference.toLowerCase().includes(q) ||
        (t.agentName ?? "").toLowerCase().includes(q);
      return matchesPriority && matchesQuery;
    });
  }, [dashboard?.openTickets, query, priorityFilter]);

  if (isLoading) {
    return (
      <div className="animate-pulse p-8">
        <div className="h-10 bg-gray-200 rounded w-1/4 mb-8"></div>
        <div className="flex gap-4">
          <div className="h-64 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return <div className="text-red-500">Failed to load dashboard.</div>;
  }

  const columns = [
    "new",
    "in_progress",
    "waiting_on_agent",
    "resolved",
  ] as const;

  const getColumnLabel = (col: string) => {
    switch (col) {
      case "new":
        return "New";
      case "in_progress":
        return "In Progress";
      case "waiting_on_agent":
        return "Waiting on Agent";
      case "resolved":
        return "Resolved";
      default:
        return col;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-700 border-red-200";
      case "medium":
        return "bg-amber-100 text-amber-700 border-amber-200";
      case "low":
        return "bg-gray-100 text-gray-700 border-gray-200";
      default:
        return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      {/* Header */}
      <header className="bg-white h-16 flex items-center justify-between px-8 border border-gray-200 shrink-0 rounded-xl mb-4 shadow-sm">
        <h1 className="text-xl font-bold text-[#0B132B]">Support Desk</h1>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search tickets..."
              className="pl-9 w-64 bg-gray-50 border-gray-200 focus-visible:ring-[#E6007E]"
            />
          </div>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-36 border-gray-200 text-gray-600 gap-2">
              <Filter className="w-4 h-4 shrink-0" />
              <SelectValue placeholder="Priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All priorities</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {/* Board */}
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-6 h-full w-max min-w-full">
          {columns.map((column) => {
            const columnTickets = filteredTickets.filter(
              (t) => t.status === column,
            );
            return (
              <div
                key={column}
                className="w-80 flex flex-col h-full bg-slate-50/50 rounded-xl border border-slate-100 p-4"
              >
                <div className="flex items-center justify-between mb-4 px-1 shrink-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-sm text-[#0B132B]">
                      {getColumnLabel(column)}
                    </h3>
                    <span className="bg-gray-200 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-full">
                      {columnTickets.length}
                    </span>
                  </div>
                  <button className="text-gray-400 hover:text-gray-600">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto space-y-3 pb-2 pr-1 custom-scrollbar">
                  {columnTickets.map((ticket) => (
                    <Card
                      key={ticket.id}
                      onClick={() => setSelectedTicketId(ticket.id)}
                      className={`border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer group hover:border-gray-300`}
                    >
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-3">
                          <Badge
                            variant="outline"
                            className={`text-[10px] font-medium border capitalize ${getPriorityColor(ticket.priority)}`}
                          >
                            {ticket.priority}
                          </Badge>
                          <span className="text-xs text-gray-400 font-medium">
                            {ticket.reference}
                          </span>
                        </div>

                        <h4 className="font-semibold text-sm text-[#0B132B] mb-4 line-clamp-2 group-hover:text-[#E6007E] transition-colors">
                          {ticket.subject}
                        </h4>

                        <div className="flex items-center justify-between mt-auto">
                          <div className="flex items-center gap-2">
                            <Avatar className="w-6 h-6 border border-gray-200">
                              <AvatarFallback className="bg-gray-100 text-[10px] text-gray-600">
                                {ticket.agentName
                                  ? ticket.agentName.charAt(0)
                                  : "A"}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex flex-col">
                              <span className="text-xs font-medium text-[#0B132B]">
                                {ticket.agentName || "Unknown Agent"}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-gray-400 text-xs">
                            <Clock className="w-3 h-3" />
                            <span>
                              {ticket.createdAt
                                ? new Date(
                                    ticket.createdAt,
                                  ).toLocaleDateString()
                                : ""}
                            </span>
                          </div>
                        </div>

                        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-1.5 text-xs">
                          <UserCheck className="w-3 h-3 text-gray-400" />
                          {ticket.assignedToId != null ? (
                            <span className="text-gray-600">
                              Assigned to{" "}
                              <span className="font-medium text-[#0B132B]">
                                {assigneeMap.get(ticket.assignedToId) ??
                                  "Account manager"}
                              </span>
                            </span>
                          ) : (
                            <span className="text-gray-400">Unassigned</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {columnTickets.length === 0 && (
                    <div className="text-center text-sm text-gray-400 py-8 border-2 border-dashed border-gray-200 rounded-lg">
                      No tickets
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <TicketDetailDialog
        ticketId={selectedTicketId}
        onClose={() => setSelectedTicketId(null)}
      />

      <style
        dangerouslySetInnerHTML={{
          __html: `
        .custom-scrollbar::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #E5E7EB; border-radius: 20px; }
        .custom-scrollbar:hover::-webkit-scrollbar-thumb { background-color: #D1D5DB; }
      `,
        }}
      />
    </div>
  );
}

function TicketDetailDialog({
  ticketId,
  onClose,
}: {
  ticketId: number | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: assignees } = useListTicketAssignees();
  const {
    data: ticket,
    isLoading,
    error,
  } = useGetTicket(ticketId ?? 0, {
    query: {
      enabled: ticketId != null,
      queryKey: getGetTicketQueryKey(ticketId ?? 0),
    },
  });
  const update = useUpdateTicket();
  const createMessage = useCreateTicketMessage();
  const [reply, setReply] = useState("");

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: getGetSupportDashboardQueryKey(),
    });
    queryClient.invalidateQueries({ queryKey: getListTicketsQueryKey() });
    if (ticketId != null) {
      queryClient.invalidateQueries({
        queryKey: getGetTicketQueryKey(ticketId),
      });
    }
  };

  const changeStatus = async (status: TicketStatus) => {
    if (ticketId == null) return;
    try {
      await update.mutateAsync({ id: ticketId, data: { status } });
      invalidate();
      toast({ title: "Status updated" });
    } catch {
      toast({
        title: "Could not update status",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const changeAssignee = async (value: string) => {
    if (ticketId == null) return;
    const assignedToId = value === UNASSIGNED ? null : Number(value);
    try {
      await update.mutateAsync({ id: ticketId, data: { assignedToId } });
      invalidate();
      toast({
        title:
          assignedToId == null ? "Ticket unassigned" : "Ticket assigned",
      });
    } catch {
      toast({
        title: "Could not update assignee",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const sendReply = async () => {
    if (ticketId == null || !reply.trim()) return;
    try {
      await createMessage.mutateAsync({
        id: ticketId,
        data: { body: reply.trim() },
      });
      invalidate();
      setReply("");
      toast({ title: "Reply sent" });
    } catch {
      toast({
        title: "Could not send reply",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  };

  const assigneeValue =
    ticket?.assignedToId != null ? String(ticket.assignedToId) : UNASSIGNED;

  return (
    <Dialog
      open={ticketId != null}
      onOpenChange={(o) => {
        if (!o) {
          setReply("");
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-[640px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B] pr-6">
            {ticket?.subject ?? "Ticket"}
          </DialogTitle>
        </DialogHeader>

        {isLoading && (
          <div className="py-10 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        )}
        {error && (
          <div className="py-6 text-sm text-red-500">
            Failed to load ticket.
          </div>
        )}

        {ticket && (
          <div className="space-y-5 py-1">
            <div className="flex items-center justify-between">
              <span className="font-mono text-xs text-gray-500">
                {ticket.reference}
              </span>
              <StatusBadge status={ticket.status} />
            </div>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Status</Label>
                <Select
                  value={ticket.status}
                  onValueChange={(v) => changeStatus(v as TicketStatus)}
                  disabled={update.isPending}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-gray-500">Assigned to</Label>
                <Select
                  value={assigneeValue}
                  onValueChange={changeAssignee}
                  disabled={update.isPending}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Unassigned" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                    {(assignees ?? []).map((a) => (
                      <SelectItem key={a.id} value={String(a.id)}>
                        {a.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="text-sm">
              <p className="text-xs text-gray-500">Agent</p>
              <p className="font-medium text-[#0B132B]">
                {ticket.agentName || "—"}
              </p>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Conversation</Label>
              <div className="max-h-64 overflow-y-auto space-y-3 rounded-xl border border-gray-100 bg-slate-50/50 p-3 custom-scrollbar">
                {ticket.messages.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-6">
                    No messages yet.
                  </p>
                )}
                {ticket.messages.map((m) => (
                  <div
                    key={m.id}
                    className="rounded-lg bg-white border border-gray-100 p-3"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-semibold text-[#0B132B]">
                        {m.sender}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {new Date(m.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">
                      {m.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-gray-500">Reply</Label>
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder="Type a reply to the agent..."
                rows={3}
              />
              <div className="flex justify-end">
                <Button
                  onClick={sendReply}
                  disabled={!reply.trim() || createMessage.isPending}
                  className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl gap-2"
                >
                  {createMessage.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Send reply
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
